import React from 'react';
import { useI18n } from '../../context/I18nContext';
import { getMultipolarLabel } from '../../utils/cableLogic';
import {
  getCatalogSectionDisplay,
  getCatalogSectionStandard
} from '../../utils/cableCatalog';
import { translateColorForDisplay } from '../../utils/colorUtils';
import CableTypeIcon from '../icons/CableTypeIcon';

const joinMeta = (parts) => parts.filter(Boolean).join(' • ');

const getMultipolarTechnicalLabel = (item) => {
  const isAWG = getCatalogSectionStandard(item) === 'awg';
  const poles = Array.from(
    { length: Number.parseInt(item?.numeroPoli, 10) || 0 },
    (_, index) => ({
      colore: item?.poliColori?.[index] || '',
      sezione: Number.parseFloat(item?.sezione) || 0.5
    })
  );
  const label = getMultipolarLabel(poles, isAWG);
  return label && isAWG ? `${label} AWG` : label;
};

const CableCatalogItemCard = ({
  item,
  isMultipolar = false,
  selectable = false,
  selected = false,
  warning = false,
  onClick,
  badge = null,
  actions = null,
  className = ''
}) => {
  const { t, language } = useI18n();
  const Root = selectable ? 'button' : 'div';
  const title = item?.codiceInterno || item?.codiceCostruttore || item?.produttore || '—';
  const primaryMeta = isMultipolar
    ? joinMeta([item?.produttore, getMultipolarTechnicalLabel(item)])
    : joinMeta([
        item?.produttore,
        translateColorForDisplay(item?.colore, language),
        getCatalogSectionDisplay(item)
      ]);
  const secondaryMeta = joinMeta([
    item?.codiceCostruttore
      ? `${t('userSettingsPage.cableCatalog.manufacturerCode')}: ${item.codiceCostruttore}`
      : '',
    isMultipolar && item?.coloreGuaina
      ? `${t('userSettingsPage.cableCatalog.outerSheathColor')}: ${translateColorForDisplay(item.coloreGuaina, language)}`
      : '',
    isMultipolar && item?.poliColori?.length
      ? `${t('userSettingsPage.cableCatalog.poleColors')}: ${item.poliColori
          .map((color) => translateColorForDisplay(color, language) || '—')
          .join(', ')}`
      : ''
  ]);

  const stateClasses = warning
    ? selected
      ? 'border-amber-500 bg-amber-50'
      : 'border-amber-300 bg-amber-50/60 hover:bg-amber-50'
    : selected
      ? 'border-blue-500 bg-blue-50'
      : 'border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/40';

  return (
    <Root
      {...(selectable ? { type: 'button', onClick } : {})}
      data-catalog-item-card={isMultipolar ? 'multipolar' : 'unipolar'}
      className={`group w-full rounded-lg border p-4 text-left transition-colors ${stateClasses} ${className}`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          {selectable && (
            <span
              aria-hidden="true"
              className={`h-4 w-4 flex-shrink-0 rounded-full border ${selected
                ? warning ? 'border-[5px] border-amber-600' : 'border-[5px] border-blue-600'
                : warning ? 'border-amber-500' : 'border-gray-400'}`}
            />
          )}

          <div className={`h-14 w-24 flex-shrink-0 rounded-lg border bg-white p-1.5 flex items-center justify-center ${
            warning ? 'border-amber-200' : selected ? 'border-blue-200' : 'border-gray-200'
          }`}>
            <CableTypeIcon
              isMultipolar={isMultipolar}
              color={item?.colore}
              jacketColor={item?.coloreGuaina}
              poleColors={item?.poliColori || []}
              poleCount={item?.numeroPoli}
              className="h-full w-full object-contain"
            />
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-sm font-semibold text-gray-900">{title}</div>
              {badge}
            </div>
            {item?.descrizione && (
              <div className="mt-1 text-sm text-gray-800">{item.descrizione}</div>
            )}
            <div className="mt-1 text-sm text-gray-600">{primaryMeta || '—'}</div>
            {secondaryMeta && (
              <div className="mt-2 text-xs text-gray-500">{secondaryMeta}</div>
            )}
          </div>
        </div>

        {actions && (
          <div className="flex flex-shrink-0 items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            {actions}
          </div>
        )}
      </div>
    </Root>
  );
};

export default CableCatalogItemCard;
